import { useSelector } from "react-redux";
import { SummaryDataProvider } from "~/Configurators/Shaft/SummaryDataProvider";
import type { RootState } from "~/redux/store";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import {
  getStep1SummaryData,
  getStep2SummaryData,
  getStep3SummaryData,
} from "~/Configurators/Shaft/metric/MetricSummaryDataProvider";
import { getHighestVisitedStep } from "~/Configurators/Linear/redux/selectors";
import {
  renderSummary,
  renderValue,
  SummaryProviderContextProvider,
  Table,
  useSummaryProvider,
} from "~/Configurators/Shaft/inch/components/ShaftSummaryDataHelper";
import {
  OnStepChangeContextProvider,
  ShaftSummaryStepFrame,
} from "~/Configurators/Shaft/components/ShaftSummaryStepFrame";
import classes from "~/Configurators/Shaft/inch/components/InchShaftSummaryData.module.less";
import { removePTags } from "~/utils/domUtils";

interface Props {
  isFullReview: boolean;
  onStepChange?: () => void;
}

export const MetricShaftSummaryData = ({ onStepChange }: Props) => {
  const xmlT = useXmlConfiguratorTranslations().t!;

  const summaryProvider = useSelector((state: RootState) => {
    return new SummaryDataProvider(state.linearConfigurator, xmlT);
  });

  const highestVisitedStep = useSelector(getHighestVisitedStep);

  return (
    <OnStepChangeContextProvider value={onStepChange}>
      <SummaryProviderContextProvider value={summaryProvider}>
        <div className={classes.summaryStepContainer}>
          {highestVisitedStep >= 0 && <Step1Summary />}
          {highestVisitedStep >= 1 && <Step2Summary />}
          {highestVisitedStep >= 2 && <Step3Summary />}
        </div>
      </SummaryProviderContextProvider>
    </OnStepChangeContextProvider>
  );
};

const Step1Summary = () => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const step1Data = getStep1SummaryData(summaryProvider) ?? [];

  return (
    <ShaftSummaryStepFrame stepNumber={1} name={xmlT("configurator.shaft.step.1.title")}>
      <Table>
        {step1Data.map((data) => (
          <tbody key={data.name?.toString()}>
            <tr>
              <td colSpan={2} dangerouslySetInnerHTML={{ __html: removePTags(data.name as string)! }} />
              <td colSpan={2}>{renderValue(data)}</td>
              <td colSpan={2}>{data.extra}</td>
            </tr>
          </tbody>
        ))}
      </Table>
    </ShaftSummaryStepFrame>
  );
};

const Step2Summary = () => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const step2Data = getStep2SummaryData(summaryProvider);

  return (
    <ShaftSummaryStepFrame stepNumber={2} name={xmlT("configurator.shaft.step.2.title")}>
      <Table>
        {step2Data.map((data) => (
          <tbody key={data.name?.toString()}>
            <tr>
              <td colSpan={2} dangerouslySetInnerHTML={{ __html: removePTags(data.name as string)! }} />
              <td colSpan={2}>{renderValue(data)}</td>
              <td colSpan={2}>{data.extra}</td>
            </tr>
          </tbody>
        ))}
      </Table>
    </ShaftSummaryStepFrame>
  );
};

const Step3Summary = () => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const step3Data = getStep3SummaryData(summaryProvider);

  const hasSomethingConfigured = step3Data.left || step3Data.middle || step3Data.right;
  if (!hasSomethingConfigured) {
    return null;
  }

  return (
    <ShaftSummaryStepFrame stepNumber={3} name={xmlT("configurator.shaft.step.3.title")}>
      <Table>
        {step3Data.left && (
          <tbody>
            <tr>
              <td dangerouslySetInnerHTML={{ __html: removePTags(step3Data.left.name as string)! }} />
              <td>{step3Data.left.value}</td>
              <td>{step3Data.left.extra}</td>
              <td colSpan={3} />
            </tr>
            {step3Data.left.axialThread && (
              <tr>
                <td colSpan={2} />
                {renderSummary(step3Data.left.axialThread.size)}
                {renderSummary(step3Data.left.axialThread.threadDepth)}
              </tr>
            )}
            {step3Data.left.chamfer && (
              <tr>
                <td colSpan={2} />
                {renderSummary(step3Data.left.chamfer.angle)}
                {renderSummary(step3Data.left.chamfer.width)}
              </tr>
            )}
          </tbody>
        )}
        {step3Data.middle && (
          <tbody>
            <tr>
              <td dangerouslySetInnerHTML={{ __html: removePTags(step3Data.middle.name as string)! }} />
              <td>{step3Data.middle.value}</td>
              <td>{step3Data.middle.extra}</td>
              {step3Data.middle.radialThread && (
                <>
                  <td>{step3Data.middle.radialThread.size.value}</td>
                  {renderSummary(step3Data.middle.radialThread.threadDepth)}
                </>
              )}
              {!step3Data.middle.radialThread && <td colSpan={4} />}
            </tr>
            {step3Data.middle.radialThread && (
              <>
                <tr>
                  <td />
                  <td />
                  {renderSummary(step3Data.middle.radialThread.threadSpacing)}
                  {renderSummary(step3Data.middle.radialThread.aL)}
                </tr>
                <tr>
                  <td colSpan={2} />
                  {renderSummary(step3Data.middle.radialThread.threadHoleAmount)}
                  {renderSummary(step3Data.middle.radialThread.aR)}
                </tr>
              </>
            )}
          </tbody>
        )}
        {step3Data.right && (
          <tbody>
            <tr>
              <td dangerouslySetInnerHTML={{ __html: removePTags(step3Data.right.name as string)! }} />
              <td>{step3Data.right.value}</td>
              <td>{step3Data.right.extra}</td>
              <td colSpan={3} />
            </tr>
            {step3Data.right.axialThread && (
              <tr>
                <td colSpan={2} />
                {renderSummary(step3Data.right.axialThread.size)}
                {renderSummary(step3Data.right.axialThread.threadDepth)}
              </tr>
            )}
            {step3Data.right.chamfer && (
              <tr>
                <td colSpan={2} />
                {renderSummary(step3Data.right.chamfer.angle)}
                {renderSummary(step3Data.right.chamfer.width)}
              </tr>
            )}
          </tbody>
        )}
      </Table>
    </ShaftSummaryStepFrame>
  );
};
